import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgkAuthService } from '@kin/ngk-auth';
import { fromEvent, merge, throttleTime } from 'rxjs';

import { AppConfigService } from 'app/app-config/app-config.service';
import { customerPortalUrlConfig } from 'app/app-config/configurations/auth-config';

import { PlatformAuthStore } from './auth.store';

function appendQueryParam(url: string, key: string, value: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

/**
 * Manages auth-related side effects:
 * - Tracks user activity to reset the idle timer
 * - Runs registered cleanup callbacks on logout or session expiration
 * - Redirects to the appropriate destination after cleanup (Customer Portal for portal customers, login page for others)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthEffectsService {
  private authService = inject(NgkAuthService);
  private authStore = inject(PlatformAuthStore);
  private appConfigService = inject(AppConfigService);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private window = this.document.defaultView!;

  private logoutCleanupCallbacks: (() => void)[] = [];
  private isManualSignOut = false;

  /** Extracted for testability — avoids a real page navigation in unit tests. */
  redirectToExternalUrl(url: string): void {
    this.window.location.href = url;
  }

  /**
   * Register a callback to run when the user's session ends.
   * Use this to clear session-scoped state (e.g., in-progress quote data).
   *
   * This service is a root singleton, so registered callbacks live for the app's lifetime
   * unless removed. Callers whose lifetime is shorter than the app (e.g., a routed component)
   * should call the returned function on teardown — typically via `DestroyRef` — so callbacks
   * don't accumulate and the registrant can be garbage-collected.
   *
   * @returns A function that unregisters the callback.
   */
  onLogout(callback: () => void): () => void {
    this.logoutCleanupCallbacks.push(callback);
    return () => {
      this.logoutCleanupCallbacks = this.logoutCleanupCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Call before `logUserOut()` when the user explicitly clicks "Sign out".
   * This causes the logout redirect to omit the `logged_out_by_inactivity` query param.
   */
  markManualSignOut(): void {
    this.isManualSignOut = true;
  }

  /**
   * Start all auth-related side effects, scoped to the caller's lifecycle: the `PlatformLayoutComponent`,
   * which is the root of all platform routes. This scopes the side effects to the platform routes only
   * and excludes the legacy routes.
   */
  start(destroyRef: DestroyRef): void {
    this.trackActivity(destroyRef);
    this.handleLogout();
  }

  private trackActivity(destroyRef: DestroyRef): void {
    merge(fromEvent(this.document, 'mousemove'), fromEvent(this.document, 'mousedown'), fromEvent(this.document, 'keydown'), fromEvent(this.document, 'touchstart'), fromEvent(this.document, 'scroll'))
      .pipe(throttleTime(5000), takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        if (!this.authStore.isIdle() && this.authStore.isLoggedIn()) {
          this.authService.activity();
        }
      });
  }

  private handleLogout(): void {
    effect(() => {
      if (this.authStore.hasLoggedOut()) {
        this.logoutCleanupCallbacks.forEach((cb) => cb());

        const userType = this.authStore.userType();
        const returnUrl = this.authStore.returnUrl();
        this.authStore.reset();

        if (userType === 'customer') {
          const environment = this.appConfigService.appContext!.environment;
          const fallbackUrl = customerPortalUrlConfig.get(environment)!;
          const destination = returnUrl ?? fallbackUrl;
          const finalUrl = this.isManualSignOut ? destination : appendQueryParam(destination, 'logged_out_by_inactivity', 'true');
          this.isManualSignOut = false;
          this.redirectToExternalUrl(finalUrl);
        } else {
          const url = this.isManualSignOut ? '/platform/login' : '/platform/login?logged_out_by_inactivity=true';
          this.isManualSignOut = false;
          this.router.navigateByUrl(url);
        }
      }
    });
  }
}
